import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../Store/actions";
import { deletePaginationCookies, scrollTableListings } from "../api_calls/utils";
import { beginListingReload, endListingReload } from "../api_calls/listingReload";
import { setBatchLoadingStatus } from "../api_calls/requestConfig";

const readJsonCookie = (cookieName) => {
    const raw = document.cookie
        .split("; ")
        .find((row) => row.startsWith(cookieName + "="));

    if (!raw) return null;
    try {
        return JSON.parse(raw.split("=")[1]);
    } catch {
        return null;
    }
};

export function useListingTableController({
    sliceSelector = (state) => state.userSelections,

    paginationReduxKey,
    paginationCookieKey,
    managePaginationCookie,
    loadPage,

    filtersReduxKey,
    filtersCookieKey,
    filterAppliedReduxKey,

    onInit,
    onReset,
    deletePaginationScope,

    enableScrollPersist = true,
    scrollPersistThrottleMs = 200,
    dataLoadedFlag = false,
    // When set (campaigns/clips/loops/stores), first paint waits for lookups + first page
    // before the table drops Loading…. Unrelated batchLoading does not use this.
    listingReloadKey,
}) {
    const dispatch = useDispatch();
    const slice = useSelector(sliceSelector);
    const pagination = slice?.[paginationReduxKey];

    // refs to avoid putting pagination + callbacks into init effect deps
    const paginationRef = useRef(pagination);
    const loadPageRef = useRef(loadPage);
    const manageCookieRef = useRef(managePaginationCookie);
    const onInitRef = useRef(onInit);
    const listingReloadKeyRef = useRef(listingReloadKey);

    useEffect(() => {
        paginationRef.current = pagination;
    }, [pagination]);

    useEffect(() => {
        loadPageRef.current = loadPage;
    }, [loadPage]);

    useEffect(() => {
        manageCookieRef.current = managePaginationCookie;
    }, [managePaginationCookie]);

    useEffect(() => {
        onInitRef.current = onInit;
    }, [onInit]);

    useEffect(() => {
        listingReloadKeyRef.current = listingReloadKey;
    }, [listingReloadKey]);

    const onPageChange = useCallback((_e, pageNumOneBased) => {
        const pageSize = paginationRef.current?.pageSize ?? 10;
        loadPageRef.current(pageNumOneBased - 1, pageSize);
    }, []);

    const onPageSizeChange = useCallback((pageSizeVal) => {
        loadPageRef.current(0, pageSizeVal);
        manageCookieRef.current?.({ pageNumber: 0, pageSize: pageSizeVal });
    }, []);

    const refresh = useCallback(() => {
        const p = paginationRef.current;
        const pageNumber = p?.pageNumber ?? 0;
        const pageSize = p?.pageSize ?? 10;
        loadPageRef.current(pageNumber, pageSize);
    }, []);

    // mount-only init
    useEffect(() => {
        // 1) pagination cookie -> redux (do NOT depend on redux pagination)
        if (paginationCookieKey && manageCookieRef.current) {
            const pag = readJsonCookie(paginationCookieKey);
            if (pag) manageCookieRef.current(pag);
        }

        // 2) filters cookie -> redux
        if (filtersCookieKey && filtersReduxKey && filterAppliedReduxKey) {
            const filt = readJsonCookie(filtersCookieKey);
            if (filt) {
                dispatch(
                    updateStateData({
                        [filtersReduxKey]: filt,
                        [filterAppliedReduxKey]: true,
                    })
                );
            }
        }

        // 3) optional resets
        onReset?.();

        // 4) first page: cookie if present, otherwise defaults
        const initialPag = paginationCookieKey ? readJsonCookie(paginationCookieKey) : null;
        const initialPageNumber = initialPag?.pageNumber ?? 0;
        const initialPageSize = initialPag?.pageSize ?? 10;

        // Lookups (hierarchy, section availability, …) run in parallel with the list
        // request. Keep both the page overlay and table Loading… until both finish.
        const loadFirstPaint = async () => {
            const reloadKey = listingReloadKeyRef.current;
            if (reloadKey) {
                beginListingReload(reloadKey);
                setBatchLoadingStatus(true);
            }
            try {
                await Promise.all([
                    Promise.resolve(onInitRef.current?.()),
                    Promise.resolve(loadPageRef.current(initialPageNumber, initialPageSize)),
                ]);
            } finally {
                if (reloadKey) {
                    endListingReload(reloadKey);
                    setBatchLoadingStatus(false);
                }
            }
        };
        loadFirstPaint();

        // 5) delete pagination cookies by scope (this will remove pagination cookies for other pages and keep the one for the currently loaded.)
        if (deletePaginationScope) {
            deletePaginationCookies(deletePaginationScope);
        }

        // 6) persist scroll
        if (!enableScrollPersist) return;

        let lastWrite = 0;
        const handleScroll = () => {
            const now = Date.now();
            if (now - lastWrite < scrollPersistThrottleMs) return;
            lastWrite = now;
            manageCookieRef.current?.({ scroll_pos: window.pageYOffset });
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
        //critical: only stable deps here
    }, [
        scrollPersistThrottleMs,
    ]);

    useEffect(() => {
        if (!paginationCookieKey) return;
        scrollTableListings(paginationCookieKey);
    }, [dataLoadedFlag]);

    return useMemo(
        () => ({
            pagination,
            onPageChange,
            onPageSizeChange,
            refresh,
        }),
        [pagination, onPageChange, onPageSizeChange, refresh]
    );
}
